import React, { useEffect, useMemo, useState } from 'react';
import { useAppContext } from '../../App';
import { api } from '../../apiService';
import { toast } from 'react-toastify';
import { Bar, Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const toDate = (v) => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const monthLabel = (d) => d.toLocaleString('en-US', { month: 'short' });

export default function FlareDashboardSection() {
  const { currentStationId } = useAppContext();
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  const [isLoading, setIsLoading] = useState(true);

  const [clients, setClients] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [bills, setBills] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [alertView, setAlertView] = useState('bills');
  const [expenseChartData, setExpenseChartData] = useState(null);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    Promise.all([
      api.get('magazine_clients').catch(() => []),
      api.get('magazine_sales_orders').catch(() => []),
      api.get('magazine_invoices').catch(() => []),
      api.get('magazine_payments').catch(() => []),
      api.get('expenses', currentStationId ? { where: { stationId: currentStationId } } : {}).catch(() => []),
      api.get('bills', currentStationId ? { where: { stationId: currentStationId } } : {}).catch(() => []),
      api.get('workers', currentStationId ? { where: { stationId: currentStationId } } : {}).catch(() => [])
    ]).then(([c, so, inv, pay, expRows, billRows, workerRows]) => {
      if (!mounted) return;
      setClients(c || []);
      setSalesOrders((so || []).map(x => ({ ...x, totalAmount: toNumber(x.totalAmount) })));
      setInvoices((inv || []).map(x => ({ ...x, totalAmount: toNumber(x.totalAmount), pendingAmount: toNumber(x.pendingAmount), totalPaid: toNumber(x.totalPaid) })));
      setPayments((pay || []).map(x => ({ ...x, amount: toNumber(x.amount) })));
      setExpenses((expRows || []).map(x => ({ ...x, amount: toNumber(x.amount) })));
      setBills(billRows || []);
      setEmployees(workerRows || []);
    }).catch((err) => {
      toast.error(`Failed to load dashboard: ${err.message}`);
    }).finally(() => {
      if (mounted) setIsLoading(false);
    });
    return () => { mounted = false; };
  }, [currentStationId]);

  const startOfMonth = useMemo(() => new Date(filterYear, filterMonth - 1, 1), [filterYear, filterMonth]);
  const endOfMonth = useMemo(() => new Date(filterYear, filterMonth, 0, 23, 59, 59), [filterYear, filterMonth]);

  const monthlySales = useMemo(() => {
    return salesOrders.filter(s => {
      const d = toDate(s.startDate);
      return d && d >= startOfMonth && d <= endOfMonth;
    });
  }, [salesOrders, startOfMonth, endOfMonth]);

  const monthlyExpenses = useMemo(() => {
    return expenses.filter(e => {
      const d = toDate(e.date);
      return d && d >= startOfMonth && d <= endOfMonth;
    });
  }, [expenses, startOfMonth, endOfMonth]);

  useEffect(() => {
    const expenseByCategory = monthlyExpenses.reduce((acc, expense) => {
      const category = expense.category || 'Miscellaneous';
      acc[category] = (acc[category] || 0) + toNumber(expense.amount);
      return acc;
    }, {});
    setExpenseChartData(Object.entries(expenseByCategory).map(([category, amount]) => ({ category, amount })));
  }, [monthlyExpenses]);

  const monthlyPayments = useMemo(() => {
    return payments.filter(p => {
      const d = toDate(p.paymentDate || p.date);
      return d && d >= startOfMonth && d <= endOfMonth;
    });
  }, [payments, startOfMonth, endOfMonth]);

  const monthlyInvoices = useMemo(() => {
    return invoices.filter(i => {
      const d = toDate(i.invoiceDate || i.createdAt);
      return d && d >= startOfMonth && d <= endOfMonth;
    });
  }, [invoices, startOfMonth, endOfMonth]);

  const kpis = useMemo(() => {
    const totalBusiness = monthlySales.reduce((sum, s) => sum + toNumber(s.totalAmount), 0);
    const totalReceived = monthlyPayments.reduce((sum, p) => sum + toNumber(p.amount), 0);
    const pending = monthlyInvoices.reduce((sum, inv) => sum + toNumber(inv.pendingAmount), 0) || Math.max(0, totalBusiness - totalReceived);
    const totalExpenses = monthlyExpenses.reduce((sum, e) => sum + toNumber(e.amount), 0);
    const net = totalReceived - totalExpenses;
    return { totalBusiness, totalReceived, pending, totalExpenses, net };
  }, [monthlySales, monthlyPayments, monthlyInvoices, monthlyExpenses]);

  const summaryCounts = useMemo(() => {
    const parseItems = (items) => {
      if (Array.isArray(items)) return items;
      if (typeof items === 'string' && items.trim()) {
        try { return JSON.parse(items); } catch { return []; }
      }
      return [];
    };
    const totalServiceCount = (salesOrders || []).reduce((sum, sale) => sum + parseItems(sale.items).length, 0);
    return {
      clients: clients.length,
      totalServices: totalServiceCount,
      totalInvoices: invoices.length,
      totalEmployees: employees.length
    };
  }, [clients.length, salesOrders, invoices.length, employees.length]);

  const last6MonthsChart = useMemo(() => {
    const labels = [];
    const business = [];
    const received = [];
    const exp = [];

    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const y = d.getFullYear();
      const m = d.getMonth();
      labels.push(monthLabel(d));

      const monthBusiness = salesOrders
        .filter(s => {
          const sd = toDate(s.startDate);
          return sd && sd.getFullYear() === y && sd.getMonth() === m;
        })
        .reduce((sum, s) => sum + toNumber(s.totalAmount), 0);

      const monthReceived = payments
        .filter(p => {
          const pd = toDate(p.paymentDate || p.date);
          return pd && pd.getFullYear() === y && pd.getMonth() === m;
        })
        .reduce((sum, p) => sum + toNumber(p.amount), 0);

      business.push(monthBusiness);
      received.push(monthReceived);

      const monthExpenses = expenses
        .filter(e => {
          const ed = toDate(e.date);
          return ed && ed.getFullYear() === y && ed.getMonth() === m;
        })
        .reduce((sum, e) => sum + toNumber(e.amount), 0);
      exp.push(monthExpenses);
    }

    return {
      labels,
      datasets: [
        {
          label: 'Total Business',
          data: business,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.5)',
          type: 'line',
          tension: 0.4,
          yAxisID: 'y1'
        },
        { label: 'Income', data: received, backgroundColor: 'rgba(22, 163, 74, 0.8)', yAxisID: 'y' },
        { label: 'Expense', data: exp, backgroundColor: 'rgba(239, 68, 68, 0.8)', yAxisID: 'y' }
      ]
    };
  }, [salesOrders, payments, expenses]);

  const last6MonthsOptions = useMemo(() => ({
    responsive: true,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { position: 'top' }, title: { display: true, text: '6-Month Business vs Income vs Expense' } },
    scales: {
      y: { type: 'linear', display: true, position: 'left', beginAtZero: true },
      y1: { type: 'linear', display: true, position: 'right', grid: { drawOnChartArea: false }, beginAtZero: true }
    }
  }), []);

  const pendingBills = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sevenDaysFromNow = new Date(today);
    sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
    return (bills || [])
      .filter(b => b.dueDate && new Date(b.dueDate) <= sevenDaysFromNow)
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  }, [bills]);

  const expiringSalesOrders = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sevenDaysFromNow = new Date(today);
    sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
    return (salesOrders || [])
      .filter(s => s.endDate && new Date(s.endDate) <= sevenDaysFromNow && new Date(s.endDate) >= today)
      .sort((a, b) => new Date(a.endDate) - new Date(b.endDate));
  }, [salesOrders]);

  const expenseBreakdownChart = useMemo(() => {
    if (!expenseChartData || expenseChartData.length === 0) return null;
    return {
      labels: expenseChartData.map(d => d.category),
      datasets: [{
        data: expenseChartData.map(d => d.amount),
        backgroundColor: ['#EF4444', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#F97316', '#84CC16'],
        borderColor: '#FFFFFF',
        borderWidth: 2
      }]
    };
  }, [expenseChartData]);

  const expenseBreakdownOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'right' }, title: { display: true, text: 'Expense Breakdown', font: { size: 16 } } }
  }), []);

  if (isLoading) {
    return <div className="flex items-center justify-center p-10"><div className="text-xl font-semibold">Loading Dashboard...</div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-800">Dashboard</h1>
        <p className="text-gray-500 mt-1">A quick overview for <span className="font-bold text-rose-600">Flare Magazine</span></p>
      </div>

      <div className="bg-white p-6 rounded-2xl shadow-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          <div>
            <label className="text-sm font-medium text-gray-600">Month</label>
            <select value={filterMonth} onChange={e => setFilterMonth(Number(e.target.value))} className="w-full p-3 bg-gray-50 rounded-md mt-1">
              {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{new Date(0, i).toLocaleString('en-US', { month: 'long' })}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Year</label>
            <select value={filterYear} onChange={e => setFilterYear(Number(e.target.value))} className="w-full p-3 bg-gray-50 rounded-md mt-1">
              {Array.from({ length: 5 }, (_, i) => <option key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4 col-span-1 lg:col-span-2">
            <Link to="/magazineSales" className="w-full bg-rose-600 text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:bg-rose-700 transition-all text-center">+ Add Sale</Link>
            <Link to="/clients" className="w-full bg-purple-600 text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:bg-purple-700 transition-all text-center">+ Add Client</Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-2xl shadow-lg flex items-center gap-4"><div className="p-3 bg-rose-100 rounded-lg"><span className="text-2xl">👤</span></div><div><p className="text-sm font-medium text-gray-500">Total Clients</p><p className="text-2xl font-bold text-gray-800">{summaryCounts.clients}</p></div></div>
        <div className="bg-white p-6 rounded-2xl shadow-lg flex items-center gap-4"><div className="p-3 bg-green-100 rounded-lg"><span className="text-2xl">🧩</span></div><div><p className="text-sm font-medium text-gray-500">Total Service</p><p className="text-2xl font-bold text-green-600">{summaryCounts.totalServices}</p></div></div>
        <div className="bg-white p-6 rounded-2xl shadow-lg flex items-center gap-4"><div className="p-3 bg-purple-100 rounded-lg"><span className="text-2xl">📄</span></div><div><p className="text-sm font-medium text-gray-500">Total Invoice</p><p className="text-2xl font-bold text-purple-700">{summaryCounts.totalInvoices}</p></div></div>
        <div className="bg-white p-6 rounded-2xl shadow-lg flex items-center gap-4"><div className="p-3 bg-blue-100 rounded-lg"><span className="text-2xl">👥</span></div><div><p className="text-sm font-medium text-gray-500">Total Employees</p><p className="text-2xl font-bold text-blue-600">{summaryCounts.totalEmployees}</p></div></div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 text-center">
          <div className="p-4 bg-blue-50 rounded-lg"><p className="text-sm font-medium text-blue-700">Total Business</p><p className="text-2xl font-bold mt-1">PKR {kpis.totalBusiness.toLocaleString()}</p></div>
          <div className="p-4 bg-green-50 rounded-lg"><p className="text-sm font-medium text-green-700">Received</p><p className="text-2xl font-bold mt-1">PKR {kpis.totalReceived.toLocaleString()}</p></div>
          <div className="p-4 bg-red-50 rounded-lg"><p className="text-sm font-medium text-red-700">Pending</p><p className="text-2xl font-bold mt-1">PKR {kpis.pending.toLocaleString()}</p></div>
          <div className="p-4 bg-purple-50 rounded-lg"><p className="text-sm font-medium text-purple-700">Expenses</p><p className="text-2xl font-bold mt-1">PKR {kpis.totalExpenses.toLocaleString()}</p></div>
          <div className={`p-4 rounded-lg ${kpis.net >= 0 ? 'bg-orange-50' : 'bg-pink-100'}`}><p className={`text-sm font-medium ${kpis.net >= 0 ? 'text-orange-800' : 'text-pink-800'}`}>Net Income</p><p className="text-2xl font-bold mt-1">PKR {kpis.net.toLocaleString()}</p></div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col">
          <div className="flex justify-between items-center mb-4 flex-shrink-0">
            <h3 className="font-bold text-lg text-gray-700">Alerts & Reminders</h3>
            <select value={alertView} onChange={e => setAlertView(e.target.value)} className="p-1 text-sm bg-gray-100 border-gray-300 rounded-md">
              <option value="bills">Bill Reminders</option>
              <option value="sales">Expiring Sales</option>
            </select>
          </div>
          <div className="space-y-3 overflow-y-auto flex-grow h-60">
            {alertView === 'bills' && (
              pendingBills.length > 0 ? pendingBills.map(bill => {
                const dueDate = new Date(bill.dueDate);
                const today = new Date(); today.setHours(0, 0, 0, 0);
                const isOverdue = dueDate < today;
                const diffTime = dueDate.getTime() - today.getTime();
                const diffDays = Math.ceil(diffTime / (1024 * 60 * 60 * 24));
                return (
                  <div key={bill.id} className="p-3 rounded-lg border-l-4 bg-gray-50" style={{ borderColor: isOverdue ? '#EF4444' : '#F59E0B' }}>
                    <div className="flex justify-between items-center">
                      <p className="font-semibold text-gray-800 text-sm">{bill.billName || 'Bill'}</p>
                      <Link to="/billManagement" className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">View</Link>
                    </div>
                    <div className="text-xs text-gray-500 mt-1 flex justify-between">
                      <span>Due: {dueDate.toLocaleDateString()}</span>
                      {isOverdue ? <span className="font-bold text-red-600">OVERDUE</span> : <span className="font-bold text-yellow-600">DUE IN {diffDays} DAYS</span>}
                    </div>
                  </div>
                );
              }) : <p className="text-sm text-gray-500 h-full flex items-center justify-center">No pending bills due soon.</p>
            )}
            {alertView === 'sales' && (
              expiringSalesOrders.length > 0 ? expiringSalesOrders.map(sale => (
                <div key={sale.id} className="flex justify-between items-center border-b pb-2">
                  <p className="font-semibold text-gray-800 text-sm">{sale.title || 'Sale Order'}</p>
                  <p className="text-xs text-yellow-700 font-bold">{new Date(sale.endDate).toLocaleDateString()}</p>
                </div>
              )) : <p className="text-sm text-gray-500 h-full flex items-center justify-center">No sales are expiring in the next 7 days.</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col">
          <div className="flex-grow h-80 md:h-full">
            <Bar data={last6MonthsChart} options={last6MonthsOptions} />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col">
          <div className="flex-grow h-80 md:h-full">
            {expenseBreakdownChart ? <Pie data={expenseBreakdownChart} options={expenseBreakdownOptions} /> : <p className="text-center text-gray-500 h-full flex items-center justify-center">No expense data for this month.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
